import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { VendorService } from '../../service/api/vendor-service';
import { CustomerService } from '../../service/api/customer-service';
import { ReceivingShipmentService } from '../../service/api/receiving-shipment-service';
import { POService } from '../../service/api/po-service';
import { partySelectGroups, mergeVendorAndCustomerParties } from './vendor-customer-parties';

function formatDateLocal(date) {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const MappingsReport = () => {
  // Filter states
  const [shipmentNumbers, setShipmentNumbers] = useState(''); // comma-separated
  const [vendors, setVendors] = useState(''); // comma-separated names
  const [poNumbers, setPONumbers] = useState(''); // comma-separated
  const [partNames, setPartNames] = useState('');
  const [releaseNames, setReleaseNames] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showAggregate, setShowAggregate] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [shipmentOptions, setShipmentOptions] = useState([]);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [poOptions, setPOOptions] = useState([]);
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState('');

  // Fetch vendors on mount
  useEffect(() => {
    async function fetchParties() {
      const [vendorResponse, customerResponse] = await Promise.all([
        VendorService.getAllVendorsList({}).catch(() => null),
        CustomerService.getAllCustomersList().catch(() => null),
      ]);
      const vendorList = vendorResponse?.status === 200 && Array.isArray(vendorResponse.response)
        ? vendorResponse.response
        : [];
      const customerList = customerResponse?.status === 200 && Array.isArray(customerResponse.response)
        ? customerResponse.response
        : [];
      const parties = mergeVendorAndCustomerParties(vendorList, customerList);
      setVendorOptions(partySelectGroups(parties).map(group => ({
        ...group,
        options: group.options.map(option => ({
          value: option.label,
          label: option.label,
          id: option.value,
        })),
      })));
    }
    fetchParties();
  }, []);

  // Fetch shipments on mount
  useEffect(() => {
    async function fetchShipments() {
      const response = await ReceivingShipmentService.getAllShipments();
      if (response && response.status === 200 && Array.isArray(response.response)) {
        setShipmentOptions(
          response.response.map(s => ({ value: s.shipmentNumber, label: s.shipmentNumber }))
        );
      }
    }
    fetchShipments();
  }, []);

  // Fetch POs when selectedVendors changes
  useEffect(() => {
    async function fetchPOs() {
      if (selectedVendors.length === 0) {
        setPOOptions([]);
        setSelectedPOs([]);
        return;
      }
      const vendorIds = selectedVendors.map(v => v.id);
      const response = await POService.getPOsByVendorIds({ vendorIds });
      if (response && response.status === 200 && Array.isArray(response.response)) {
        setPOOptions(response.response.map(po => ({ value: po.poNumber, label: po.poNumber })));
      }
    }
    fetchPOs();
  }, [selectedVendors]);

  // Update vendors filter when selection changes
  useEffect(() => {
    setVendors(selectedVendors.map(v => v.value).join(','));
  }, [selectedVendors]);

  // Update shipmentNumbers filter when selection changes
  useEffect(() => {
    setShipmentNumbers(selectedShipments.map(s => s.value).join(','));
  }, [selectedShipments]);

  // Update poNumbers filter when selection changes
  useEffect(() => {
    setPONumbers(selectedPOs.map(po => po.value).join(','));
  }, [selectedPOs]);

  // Handler for Get Report button
  const handleGetReport = async () => {
    setLoading(true);
    setError('');
    setReportData([]);
    try {
      const payload = {
        ShipmentNumbers: shipmentNumbers || null,
        Vendors: vendors || null,
        PONumbers: poNumbers || null,
        PartNames: partNames || null,
        ReleaseNames: releaseNames || null,
        FromDate: formatDateLocal(fromDate),
        ToDate: formatDateLocal(toDate),
        ShowAggregate: showAggregate
      };
      const response = await ReceivingShipmentService.getReport(payload);
      if (response && response.status === 200 && Array.isArray(response.response)) {
        setReportData(response.response);
      } else {
        setError(response?.errorMessage?.message || 'Failed to fetch report');
      }
    } catch (err) {
      setError('Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-4">
      <h4>Shipment Receiving Report</h4>
      <div className="row mb-3">
        <div className="col-md-2">
          <label>Shipment Number</label>
          <Select
            isMulti
            options={shipmentOptions}
            value={selectedShipments}
            onChange={setSelectedShipments}
            classNamePrefix="react-select"
            placeholder="Select Shipment(s)"
          />
        </div>
        <div className="col-md-2">
          <label>Vendor/Customer</label>
          <Select
            isMulti
            options={vendorOptions}
            value={selectedVendors}
            onChange={setSelectedVendors}
            classNamePrefix="react-select"
            placeholder="Select vendor or customer"
          />
        </div>
        <div className="col-md-2">
          <label>PO Number</label>
          <Select
            isMulti
            options={poOptions}
            value={selectedPOs}
            onChange={setSelectedPOs}
            classNamePrefix="react-select"
            placeholder="Select PO(s)"
          />
        </div>
        <div className="col-md-2">
          <label>Part Name</label>
          <input type="text" className="form-control" value={partNames} onChange={e => setPartNames(e.target.value)} placeholder="e.g. PartABC, XYZ" />
        </div>
        <div className="col-md-2">
          <label>Release Name</label>
          <input type="text" className="form-control" value={releaseNames} onChange={e => setReleaseNames(e.target.value)} placeholder="e.g. jan release" />
        </div>
        <div className="col-md-1">
          <label>From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={date => setFromDate(date)}
            dateFormat="MM/dd/yyyy"
            className="form-control"
            placeholderText="MM/DD/YYYY"
            autoComplete="off"
            isClearable
          />
        </div>
        <div className="col-md-1">
          <label>To Date</label>
          <DatePicker
            selected={toDate}
            onChange={date => setToDate(date)}
            dateFormat="MM/dd/yyyy"
            className="form-control"
            placeholderText="MM/DD/YYYY"
            autoComplete="off"
            isClearable
          />
        </div>
      </div>
      <div className="mb-3 d-flex align-items-center gap-3">
        <label>
          <input
            type="checkbox"
            checked={showAggregate}
            onChange={e => setShowAggregate(e.target.checked)}
            className="me-2"
          />
          Display Part Aggregate Count
        </label>
        <button className="btn btn-primary ms-auto" onClick={handleGetReport} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
          Get Report
        </button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Shipment Number</th>
              <th>Vendor/Customer</th>
              <th>PO Number</th>
              <th>Part Name</th>
              <th>Release Name</th>
              <th>Received Date</th>
              <th>Requested Qty</th>
              <th>Received Qty</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center">Loading...</td></tr>
            ) : reportData.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted">No data to display. Apply filters and fetch data.</td>
              </tr>
            ) : (
              reportData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.shipmentNumber}</td>
                  <td>{row.vendorName}</td>
                  <td>{row.poNumber}</td>
                  <td>{row.partName}</td>
                  <td>{row.releaseName}</td>
                  <td>{row.receivedDate}</td>
                  <td>{row.requestedQty}</td>
                  <td>{row.receivedQty}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MappingsReport;
